// Create or edit a route. Ring groups only for now — the form is per-kind on
// purpose, because an IVR's fields (menu options, prompt, timeout) have almost
// nothing in common with a ring group's, and one merged form would be a maze of
// conditionals long before the third type.
//
// Members are picked from the customer's own extensions rather than typed. A ring
// group whose member doesn't exist rings nothing at that step, silently, and the
// PBX won't complain — so the roster is the input.
//
// The route lives on ONE PBX host, taken from the customer's extensions: a member
// on another host can't be in the group, and the number that rings it has to be
// delivered to the same host.

import { useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import { api } from '../../lib/api.js';

// FreePBX ring strategies, in the order an operator is likely to want them.
// Labels say what happens, not what FreePBX calls it.
const STRATEGIES = [
  { value: 'ringall',         label: 'Ring all at once' },
  { value: 'hunt',            label: 'Ring in order, one at a time' },
  { value: 'memoryhunt',      label: 'Ring in order, adding each phone' },
  { value: 'firstavailable',  label: 'First available phone only' },
  { value: 'firstnotonphone', label: 'First phone not already on a call' },
  { value: 'random',          label: 'Random' },
];

// One form per kind. Ring groups are the only one built; anything else is
// refused outright rather than shown a ring-group form under another name — a
// silent fallback would create the wrong object the day a new kind is listed as
// creatable before its form exists.
export default function RouteEditor({ route, kind = 'ring_group', onClose, onSaved }) {
  const routeKind = route?.kind || kind;
  const editing = Boolean(route?.id);
  const [subscriberId, setSubscriberId] = useState(route?.subscriberId ?? '');
  const [number, setNumber] = useState(route?.number ?? '');
  const [name, setName] = useState(route?.name ?? '');
  const [strategy, setStrategy] = useState(route?.config?.strategy ?? 'ringall');
  const [ringTime, setRingTime] = useState(route?.config?.ringTime ?? 20);
  const [members, setMembers] = useState(route?.targets ?? []);
  const [memberSearch, setMemberSearch] = useState('');
  const [err, setErr] = useState(null);

  const subsQ = useQuery({ queryKey: ['subscribers'], queryFn: () => api.get('/subscribers') });
  const subscribers = useMemo(() => {
    const d = subsQ.data;
    return Array.isArray(d) ? d : (d?.subscribers || d?.rows || []);
  }, [subsQ.data]);

  // The chosen customer's extensions — the only legal members.
  const extQ = useQuery({
    queryKey: ['subscriber', String(subscriberId), 'sip-accounts'],
    queryFn: () => api.get('/sip-accounts', { subscriber_id: subscriberId }),
    enabled: Boolean(subscriberId),
  });
  const extensions = useMemo(() => {
    const d = extQ.data;
    const list = Array.isArray(d) ? d : (d?.sip_accounts || d?.rows || []);
    return list.filter((e) => e.status !== 'deleted');
  }, [extQ.data]);

  // What's left to add, narrowed by the search box. Matches on the extension
  // number or its location label, since an operator thinks "the shop phone".
  const available = useMemo(() => {
    const q = memberSearch.trim().toLowerCase();
    return extensions
      .filter((e) => !members.includes(e.username))
      .filter((e) => !q
        || String(e.username).toLowerCase().includes(q)
        || String(e.locationLabel || '').toLowerCase().includes(q));
  }, [extensions, members, memberSearch]);

  // Changing customer invalidates the member list — those extensions belong to
  // someone else. Keeps a half-edited form from saving a cross-customer group.
  useEffect(() => {
    if (!editing) setMembers([]);
  }, [subscriberId, editing]);

  const save = useMutation({
    mutationFn: () => {
      const body = {
        grpnum: String(number).trim(),
        name: name.trim() || null,
        strategy,
        ring_time: Number(ringTime),
        members,
      };
      return editing
        ? api.patch(`/ring-groups/${route.id}`, body)
        : api.post(`/subscribers/${subscriberId}/ring-groups`, body);
    },
    onSuccess: () => { setErr(null); onSaved?.(); },
    onError: (e) => setErr(e?.body?.error || e?.message || 'Could not save that.'),
  });

  const toggleMember = (username) => setMembers((m) => (
    m.includes(username) ? m.filter((x) => x !== username) : [...m, username]
  ));
  // Order matters for the in-order strategies, so it's editable rather than
  // implied by the roster's order.
  const move = (i, delta) => setMembers((m) => {
    const j = i + delta;
    if (j < 0 || j >= m.length) return m;
    const out = [...m];
    [out[i], out[j]] = [out[j], out[i]];
    return out;
  });

  const ordered = strategy === 'hunt' || strategy === 'memoryhunt';
  const canSave = Boolean(String(number).trim()) && (editing || Boolean(subscriberId));

  if (routeKind !== 'ring_group') {
    return (
      <div className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-black/50 p-4">
        <div className="my-10 w-full max-w-md rounded-lg bg-white p-6 shadow-xl">
          <h3 className="text-lg font-bold text-gray-900">Not editable here yet</h3>
          <p className="mt-2 text-sm text-gray-600">
            Verso doesn&rsquo;t build this kind of route yet — it&rsquo;s still managed on the PBX.
          </p>
          <div className="mt-4 flex justify-end">
            <button
              type="button"
              onClick={onClose}
              className="rounded-md border border-gray-300 px-4 py-1.5 text-sm font-medium text-gray-700 hover:bg-gray-50"
            >
              Close
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-black/50 p-4">
      <div className="my-10 w-full max-w-lg rounded-lg bg-white p-6 shadow-xl">
        <h3 className="text-lg font-bold text-gray-900">
          {editing ? `Edit ${route.name || `ring group ${route.number}`}` : 'New ring group'}
        </h3>
        <p className="mt-1 text-sm text-gray-600">
          Rings several of a customer&rsquo;s extensions from one number.
        </p>

        <div className="mt-4 space-y-4">
          {!editing && (
            <div>
              <label htmlFor="rg-customer" className="block text-sm font-medium text-gray-700">Customer</label>
              <select
                id="rg-customer"
                value={subscriberId}
                onChange={(e) => setSubscriberId(e.target.value)}
                className="mt-1 w-full rounded-md border border-gray-300 px-3 py-1.5 text-sm"
              >
                <option value="">Choose a customer…</option>
                {subscribers.map((s) => (
                  <option key={s.id} value={s.id}>{s.name || s.display_name || `#${s.id}`}</option>
                ))}
              </select>
            </div>
          )}

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label htmlFor="rg-number" className="block text-sm font-medium text-gray-700">
                Route number
              </label>
              <input
                id="rg-number"
                value={number}
                onChange={(e) => setNumber(e.target.value)}
                placeholder="600"
                className="mt-1 w-full rounded-md border border-gray-300 px-3 py-1.5 text-sm font-mono"
              />
              <p className="mt-1 text-xs text-gray-500">
                What staff dial internally to reach the group.
              </p>
              {/* Leading zeros are this org's convention and they create, read and
                  update fine (verified live 2026-08-01). Only DELETE can't handle
                  them, so that's the one thing worth saying — and only once the
                  number is committed, not while it's being typed. */}
              {editing && /^0\d/.test(String(number).trim()) && (
                <p className="mt-1 text-xs text-gray-500">
                  Numbers starting with 0 work everywhere except deletion — the phone system’s API drops
                  the leading zero when deleting, so removing this group has to be done on the PBX
                  itself.
                </p>
              )}
            </div>
            <div>
              <label htmlFor="rg-name" className="block text-sm font-medium text-gray-700">Name</label>
              <input
                id="rg-name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Front desk"
                className="mt-1 w-full rounded-md border border-gray-300 px-3 py-1.5 text-sm"
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label htmlFor="rg-strategy" className="block text-sm font-medium text-gray-700">How it rings</label>
              <select
                id="rg-strategy"
                value={strategy}
                onChange={(e) => setStrategy(e.target.value)}
                className="mt-1 w-full rounded-md border border-gray-300 px-3 py-1.5 text-sm"
              >
                {STRATEGIES.map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}
              </select>
            </div>
            <div>
              <label htmlFor="rg-ringtime" className="block text-sm font-medium text-gray-700">
                Ring for (seconds)
              </label>
              <input
                id="rg-ringtime"
                type="number" min="5" max="300"
                value={ringTime}
                onChange={(e) => setRingTime(e.target.value)}
                className="mt-1 w-full rounded-md border border-gray-300 px-3 py-1.5 text-sm"
              />
              <p className="mt-1 text-xs text-gray-500">Then the caller falls through to voicemail.</p>
            </div>
          </div>

          <div>
            <span className="block text-sm font-medium text-gray-700">Extensions it rings</span>
            {!subscriberId && !editing && (
              <p className="mt-1 text-xs text-gray-500">Choose a customer first.</p>
            )}
            {subscriberId && extQ.isLoading && (
              <p className="mt-1 text-xs text-gray-400">Loading their extensions…</p>
            )}
            {subscriberId && !extQ.isLoading && extensions.length === 0 && (
              <p className="mt-1 rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-900">
                This customer has no extensions yet. A group with nothing in it rings nobody.
              </p>
            )}
            {/* What's in the group comes first, in ring order, each removable.
                Extensions on a PBX are 10-digit numbers here, so a roster of forty
                is unreadable as one flat checkbox list — the operator needs to see
                the four that matter, then search for the fifth. */}
            {members.length > 0 && (
              <ol className="mt-1.5 divide-y divide-gray-100 rounded-md border border-gray-300 bg-gray-50/60">
                {members.map((m, i) => {
                  const ext = extensions.find((e) => e.username === m);
                  return (
                    <li key={m} className="flex items-center gap-2 px-3 py-2 text-sm">
                      {ordered && <span className="w-4 text-xs text-gray-400">{i + 1}</span>}
                      <span className="min-w-0 flex-1">
                        <span className="font-mono text-gray-900">{m}</span>
                        {ext?.locationLabel && <span className="ml-2 text-xs text-gray-500">{ext.locationLabel}</span>}
                        {/* A member Verso doesn't recognise is usually an external
                            number FreePBX dials with a confirm suffix. Keep it,
                            label it — dropping it on save would quietly change
                            who the group rings. */}
                        {!ext && (
                          <span className="ml-2 text-xs text-amber-800" title="Not one of this customer's extensions — kept as-is">
                            external
                          </span>
                        )}
                      </span>
                      {ordered && (
                        <>
                          <button
                            type="button" onClick={() => move(i, -1)} disabled={i === 0}
                            className="rounded px-1.5 text-gray-500 hover:bg-gray-200 disabled:opacity-30"
                            aria-label={`Move ${m} earlier`}
                          >↑</button>
                          <button
                            type="button" onClick={() => move(i, 1)} disabled={i === members.length - 1}
                            className="rounded px-1.5 text-gray-500 hover:bg-gray-200 disabled:opacity-30"
                            aria-label={`Move ${m} later`}
                          >↓</button>
                        </>
                      )}
                      <button
                        type="button"
                        onClick={() => toggleMember(m)}
                        className="rounded px-1.5 text-xs font-medium text-red-700 hover:bg-red-50"
                        aria-label={`Remove ${m} from the group`}
                      >Remove</button>
                    </li>
                  );
                })}
              </ol>
            )}

            {/* Then what you can add, filtered. */}
            {extensions.length > 0 && (
              <>
                <input
                  type="text"
                  value={memberSearch}
                  onChange={(ev) => setMemberSearch(ev.target.value)}
                  placeholder="Search extensions to add"
                  aria-label="Search extensions to add"
                  className="mt-2 w-full rounded-md border border-gray-300 px-3 py-1.5 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
                />
                {available.length === 0 ? (
                  <p className="mt-1.5 text-xs text-gray-500">
                    {memberSearch.trim()
                      ? `No extension matches “${memberSearch}”.`
                      : 'Every extension this customer has is already in the group.'}
                  </p>
                ) : (
                  <ul className="mt-1.5 max-h-56 divide-y divide-gray-100 overflow-y-auto rounded-md border border-gray-200">
                    {available.map((e) => (
                      <li key={e.id}>
                        <button
                          type="button"
                          onClick={() => toggleMember(e.username)}
                          className="flex w-full items-center justify-between gap-2 px-3 py-2 text-left text-sm hover:bg-blue-50/60"
                        >
                          <span className="min-w-0">
                            <span className="font-mono text-gray-900">{e.username}</span>
                            {e.locationLabel && <span className="ml-2 text-xs text-gray-500">{e.locationLabel}</span>}
                          </span>
                          <span className="flex-shrink-0 text-xs font-medium text-blue-700">Add</span>
                        </button>
                      </li>
                    ))}
                  </ul>
                )}
              </>
            )}
          </div>

          {members.length === 0 && (
            <p className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-700">
              With no extensions chosen, a call reaching this route rings nobody.
            </p>
          )}

          {/* This used to say the PBX half had to be done by hand. The agent's
              ring-group endpoints are live now, so saving builds it — and the
              response says whether that worked. */}
          <p className="rounded-md border border-gray-200 bg-gray-50 px-3 py-2 text-xs text-gray-600">
            Saving records the route in Verso and builds it on the PBX. If the host can&rsquo;t be
            reached the route is still saved, and the Routes list will show it as not on the PBX yet.
          </p>

          {err && <p className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">{err}</p>}
        </div>

        <div className="mt-5 flex justify-end gap-3">
          <button
            onClick={onClose}
            disabled={save.isPending}
            className="rounded-md border border-gray-300 px-4 py-1.5 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            onClick={() => save.mutate()}
            disabled={!canSave || save.isPending}
            className="rounded-md bg-blue-600 px-4 py-1.5 text-sm font-medium text-white hover:bg-blue-700 disabled:opacity-50"
          >
            {save.isPending ? 'Saving…' : (editing ? 'Save route' : 'Create route')}
          </button>
        </div>
      </div>
    </div>
  );
}
